/**
 * Utility functions to convert field mappings data to API format
 */

/**
 * Parse source field API name to extract object label and API name
 * @param {String} value - Source field value (e.g., "companies.industry" or "dealstage")
 * @returns {Object} - Object with objectLabel and apiName
 */
const parseSourceFieldApiName = (value) => {
  if (!value) return { apiName: "", objectLabel: "" };
  const parts = String(value).split(".");
  if (parts.length > 1) {
    return { objectLabel: parts[0].toLowerCase(), apiName: parts.slice(1).join(".") };
  }
  return { apiName: value, objectLabel: "" };
};

/**
 * Get label for source field
 * @param {String} sourceFieldApiName - Source field API name
 * @param {Array} sourceFields - Array of source field objects
 * @returns {String} - Formatted label (e.g., "Companies#About Us" or "Deal Stage")
 */
const getSourceFieldLabel = (sourceFieldApiName, sourceFields = []) => {
  if (!sourceFieldApiName) return "";

  const { apiName, objectLabel } = parseSourceFieldApiName(sourceFieldApiName);

  // Find the source field
  const sourceFieldCandidates = sourceFields.filter((sf) => sf.apiName === apiName);
  const sourceField =
    sourceFieldCandidates.find((sf) => objectLabel && sf.objectLabel === objectLabel) ||
    sourceFieldCandidates.find((sf) => Array.isArray(sf.options) && sf.options.length > 0) ||
    sourceFieldCandidates[0];

  if (!sourceField) return "";

  // Format label based on objectLabel
  // If objectLabel exists and is not "deals", use format "ObjectLabel#FieldLabel"
  // Otherwise, just use the field label
  if (sourceField.objectLabel && sourceField.objectLabel.toLowerCase() !== "deals") {
    const formattedObjectLabel = sourceField.objectLabel.charAt(0).toUpperCase() + sourceField.objectLabel.slice(1);
    return `${formattedObjectLabel}#${sourceField.label}`;
  }

  return sourceField.label || "";
};

/**
 * Convert field mappings to mapping_json format
 * @param {Array} fieldMappings - Array of field mapping objects
 * @param {Array} sourceFields - Array of source field objects (for getting labels)
 * @returns {Array} - Array of mapping objects in the format expected by the API
 */
export const convertToMappingJson = (fieldMappings, sourceFields = []) => {
  // Include all field mappings, even those without sourceField or defaultValue
  return fieldMappings.map((mapping) => {
    const targetField = mapping.targetField;
    const sourceFieldApiName = mapping.sourceFieldApiName || "";
    const label = getSourceFieldLabel(sourceFieldApiName, sourceFields);

    return {
      value: null,
      type: targetField.type,
      targetField: targetField.apiName,
      sourceField: sourceFieldApiName,
      isGlobal: mapping.isGlobal || false,
      defaultValue: mapping.defaultValue || "",
      label: label,
    };
  });
};

/**
 * Convert field mappings to picklist_mapping_json format
 * @param {Array} fieldMappings - Array of field mapping objects
 * @param {Object} picklistMappings - Object containing picklist mappings keyed by field API name
 * @returns {Object} - Object with field names as keys and arrays of picklist mappings as values
 */
export const convertToPicklistMappingJson = (fieldMappings, picklistMappings = {}) => {
  const result = {};

  fieldMappings.forEach((mapping) => {
    const targetFieldApiName = mapping.targetField.apiName;
    const fieldType = mapping.targetField.type;

    // Include all picklist and multipicklist fields
    if (fieldType === "PICKLIST" || fieldType === "MULTIPICKLIST") {
      // If there are picklist mappings for this field, include them
      if (picklistMappings[targetFieldApiName] && picklistMappings[targetFieldApiName].length > 0) {
        result[targetFieldApiName] = picklistMappings[targetFieldApiName].map((picklistMapping) => ({
          targetValue: picklistMapping.targetValue,
          targetField: targetFieldApiName,
          sourceValue: picklistMapping.sourceValue,
        }));
      } else {
        // Include empty array for picklist fields without mappings
        result[targetFieldApiName] = [];
      }
    }
  });

  return result;
};

/**
 * Convert pipeline picklist mappings to API format
 * @param {Object} pipelinePicklistMappings - Object with pipelineId as keys and picklist mappings as values
 * @returns {Array} - Array of pipeline picklist mapping objects
 */
export const convertToPipelinePicklistMappings = (pipelinePicklistMappings = {}) => {
  if (!pipelinePicklistMappings || typeof pipelinePicklistMappings !== 'object') {
    return [];
  }

  return Object.keys(pipelinePicklistMappings).map((pipelineId) => ({
    pipeline_id: pipelineId,
    picklist_mapping_json: pipelinePicklistMappings[pipelineId] || {},
  }));
};

/**
 * Build the complete request body for the field mappings API
 * @param {String} mappingName - Name of the mapping profile
 * @param {Array} fieldMappings - Array of field mapping objects
 * @param {Object} picklistMappings - Object containing picklist mappings keyed by field API name
 * @param {Object} pipelinePicklistMappings - Object containing pipeline-specific picklist mappings
 * @param {Array} sourceFields - Array of source field objects (for getting labels)
 * @returns {Object} - Complete request body for the API
 */
export const buildFieldMappingsRequestBody = (mappingName, fieldMappings, picklistMappings = {}, pipelinePicklistMappings = {}, sourceFields = []) => {
  return {
    name: mappingName,
    mapping_json: convertToMappingJson(fieldMappings, sourceFields),
    picklist_mapping_json: convertToPicklistMappingJson(fieldMappings, picklistMappings),
    pipeline_picklist_mappings: convertToPipelinePicklistMappings(pipelinePicklistMappings),
  };
};

/**
 * Build request body for auto-save (without the name field)
 * @param {Array} fieldMappings - Array of field mapping objects
 * @param {Object} picklistMappings - Object containing picklist mappings keyed by field API name
 * @param {Object} pipelinePicklistMappings - Object containing pipeline-specific picklist mappings
 * @param {Array} sourceFields - Array of source field objects (for getting labels)
 * @returns {Object} - Request body for auto-save (without name field)
 */
export const buildAutoSaveRequestBody = (fieldMappings, picklistMappings = {}, pipelinePicklistMappings = {}, sourceFields = []) => {
  return {
    mapping_json: convertToMappingJson(fieldMappings, sourceFields),
    picklist_mapping_json: convertToPicklistMappingJson(fieldMappings, picklistMappings),
    pipeline_picklist_mappings: convertToPipelinePicklistMappings(pipelinePicklistMappings),
  };
};
